import BaseService from '../../base/base_service';
import Event from '../../utils/event';
import _ from '../../utils/underscore';
import {SYSTEM_EVENTS} from '../../events/events';
import { BalanceType } from '../../../../common/const/wallet_balance_type';

class WalletService extends BaseService {
    /**
     * @static
     * @type {string}
     */
    static CLASS = 'WalletService';

    _previousBalance = null;

    /**
     *
     * @param params
     */
    init(params) {
        super.init(params);
        this._subscribe();
    }


    /**
     *
     *
     * @description subscribe language service events.
     * @private
     */
    _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.SESSION_EVENT_WALLET, (data) => {
            this.refreshWallet(data);
        });


        this.onSystemEvent([SYSTEM_EVENTS.ON_INIT_COMPLETE, SYSTEM_EVENTS.OAUTH_STATE_CHANGE], () => {
           this._updateWalletLocally();
        });
    }


    /**
     * Update wallet data and refresh balance from local storage
     * @private
     */
    _updateWalletLocally() {
        if (this.serviceFactory.oAuth2Service().isAuthorized()) {
            this.refreshWallet();
            return;
        }
       this._previousBalance = null;
    }

    /**
     * Refresh wallet.
     */
    refreshWallet(bankroll = {}) {
        let originalWalletData = _.safeJSON(this._previousBalance);
        let newWalletData = this._updateWallet(bankroll.Balance, bankroll.Currency, bankroll.RealMoneyBankroll);

        const isNewBCurrency = !_.isNullOrUndefined(bankroll.Currency) &&
                             (_.isNullOrUndefined(originalWalletData) || (!_.isNullOrUndefined(originalWalletData) && originalWalletData.currency !== bankroll.Currency));

        if (isNewBCurrency){
              Event.fire(SYSTEM_EVENTS.CURRENCY_UPDATED, newWalletData.currency);
        }

        const isNewBalance = !_.isNullOrUndefined(bankroll.Balance) &&
          (_.isNullOrUndefined(originalWalletData) || (_.safeStringToNumber(originalWalletData.balance) !== _.safeStringToNumber(bankroll.Balance)));
        const isNewRealMoneyBankroll = !_.isNullOrUndefined(bankroll.RealMoneyBankroll) &&
          (_.isNullOrUndefined(originalWalletData) || (_.safeStringToNumber(originalWalletData.RealMoneyBankroll) !== _.safeStringToNumber(bankroll.RealMoneyBankroll)));

      if  (isNewBalance || isNewRealMoneyBankroll) {
            Event.fire(SYSTEM_EVENTS.BALANCE_UPDATED, newWalletData.balance);
        }
    }


    /**
     * Update balance and currency in local storage
     * Strategy to update / initialize wallet data
     * 1. Get from params (real time update)
     * 2. Get from local storage (updated before refresh)
     * 3. Get from token (updated on last Login)
     * @private
     */
    _updateWallet(balance, currency, RealMoneyBankroll) {
        let walletData = _.safeJSON(this._previousBalance);
        let tokenWallet = this._getTokenWalletData();

        if (_.isUndefined(walletData) || _.isNull(walletData)) {
            walletData = {};
            if (!_.isNull(tokenWallet)) {
                walletData.balance = tokenWallet.AvailableMoney;
                walletData.currency = tokenWallet.CurrencyCode;
                walletData.RealMoneyBankroll = tokenWallet.RealMoneyBankroll;
            }
        }

        walletData.balance = !_.isNullOrUndefined(balance) ? Math.floor(balance) : walletData.balance;
        walletData.currency = !_.isNullOrUndefined(currency) ? currency : walletData.currency;
        walletData.RealMoneyBankroll = !_.isNullOrUndefined(RealMoneyBankroll) ? Math.floor(RealMoneyBankroll) : walletData.RealMoneyBankroll;
        this._previousBalance = JSON.stringify(walletData);

        return walletData;
    }


    /**
     * Get wallet data from local storage if exist.
     *
     * @private
     */
    _getTokenWalletData() {
        return _.safeJSON(this.serviceFactory.oAuth2Service().extractTokenProperty('walletData'), null);
    }


    /**
     * Get the wallet data that exist in the model.
     *
     * @returns {*}
     * @public
     */
    getBalance(balanceType) {
        balanceType = _.isNullOrUndefined(balanceType) ? BalanceType.BOTH : balanceType;
        let balanceProperties = _.safeJSON(this._previousBalance, {});
        let balance;
        let tokenWalletData = this._getTokenWalletData();

        switch (balanceType) {
            case BalanceType.CASH:
                balance = balanceProperties.RealMoneyBankroll;
                if (_.isUndefined(balance) && !_.isNull(tokenWalletData)) {
                    balance = tokenWalletData.RealMoneyBankroll;
                }
                break;
            case BalanceType.BONUS:
                balance = balanceProperties.balance - balanceProperties.RealMoneyBankroll;
                if (_.isUndefined(balance) && !_.isNull(tokenWalletData)) {
                    balance = tokenWalletData.AvailableMoney - tokenWalletData.RealMoneyBankroll;
                }
                break;
            case BalanceType.BOTH:
            default:
                balance = balanceProperties.balance;
                if (_.isUndefined(balance) && !_.isNull(tokenWalletData)) {
                    balance = tokenWalletData.AvailableMoney;
                }
        }

        if (_.isNullOrUndefined(balance)) {
            throw Error('Could not get balance.');
        }

        return balance;
    }


    /**
     * Get active currency code.
     *
     * @returns {string}
     */
    getCurrency() {
        let currency = this.serviceFactory.currencyService().getCurrency();

        return currency.toUpperCase();
    }

    getBonusBalance() {
        return this.getBalance(BalanceType.BONUS);
    }

    getCashBalance() {
        return this.getBalance(BalanceType.CASH);
    }

    /**
     * Get current bankroll (balance) string appropriate to currency symbol and thousand and decimal separators
     *
     * @return {string|number}
     */
    getBankroll(bankrollType = BalanceType.BOTH, plainNumber = false) {
        if (!this.serviceFactory.oAuth2Service().isAuthorized()) {
            return null;
        }

        if(plainNumber){
            return this.getBalance(bankrollType);
        }

        return this.serviceFactory.currencyService().formatMoney(this.getBalance(bankrollType));
    }
}

export default WalletService;